import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, fmtMoney } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Loader2, FileCheck2, Download } from "lucide-react";

export default function SignLease() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [lot, setLot] = useState(null);
  const [signature, setSignature] = useState("");
  const [agree, setAgree] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then(async (r) => {
      setBooking(r.data);
      const l = await api.get(`/lots/${r.data.lot_number}`);
      setLot(l.data);
    }).catch(() => { toast.error("Booking not found"); navigate("/"); });
  }, [bookingId, navigate]);

  const sign = async () => {
    if (!signature.trim()) return toast.error("Please type your name to sign");
    if (!agree) return toast.error("Please confirm you've read the lease");
    setSigning(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/sign`, { signature_name: signature });
      setBooking(data);
      toast.success("Lease signed successfully");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign");
    } finally { setSigning(false); }
  };

  if (!booking || !lot) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B0C0E]" /></div>;
  }

  const signed = booking.status === "active";
  const pdfUrl = `${API}/bookings/${bookingId}/lease.pdf`;

  return (
    <div data-testid="sign-lease-page" className="min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <div className="overline">Lease Agreement</div>
        <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl uppercase tracking-tight text-[#0B0C0E]">
          {signed ? "Your Signed Lease" : "Sign Your Lease"}
        </h1>
        <p className="mt-3 text-[#56606E] text-sm">Booking #{bookingId.slice(0, 8)} · Lot {lot.lot_number}</p>

        <div className="mt-8 border-2 border-[#0B0C0E] bg-white h-[600px] overflow-hidden" data-testid="lease-pdf-frame">
          <iframe src={pdfUrl} title="Lease PDF" className="w-full h-full" />
        </div>

        <div className="mt-4 flex gap-3">
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#2C2F36] hover:text-[#0B0C0E] underline underline-offset-4 decoration-[#FACC15] decoration-2" data-testid="download-pdf-link">
            <Download className="w-4 h-4" /> Download PDF
          </a>
        </div>

        {!signed ? (
          <div className="mt-10 border-2 border-[#0B0C0E] bg-white p-8" data-testid="sign-form">
            <h2 className="font-display font-black text-2xl uppercase tracking-tight text-[#0B0C0E]">Electronic Signature</h2>
            <p className="mt-2 text-sm text-[#56606E]">Type your full legal name. This constitutes a binding electronic signature under Alberta&apos;s Electronic Transactions Act.</p>

            <label className="overline mt-6 block">Type your full name</label>
            <input
              value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="John A. Doe"
              className="mt-2 w-full bg-white border-b-2 border-[#0B0C0E] focus:border-[#FACC15] outline-none px-2 py-4 text-2xl font-display italic text-[#0B0C0E]"
              data-testid="signature-input"
            />

            <label className="flex items-start gap-3 mt-6 text-sm text-[#2C2F36] cursor-pointer">
              <input
                type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                className="mt-1 accent-[#FACC15] w-4 h-4"
                data-testid="agree-checkbox"
              />
              I have read and agree to the terms of this 14 Acres Commercial / Industrial Lot Lease Agreement.
            </label>

            <button
              onClick={sign} disabled={signing}
              className="mt-8 bg-[#0B0C0E] hover:bg-black disabled:opacity-50 text-white font-bold px-10 py-4 uppercase tracking-wide inline-flex items-center gap-3 border-l-4 border-[#FACC15]"
              data-testid="sign-lease-button"
            >
              {signing ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing…</> : "Sign Lease"}
            </button>
          </div>
        ) : (
          <div className="mt-10 border-2 border-emerald-600 bg-emerald-50 p-8 flex items-start gap-4" data-testid="signed-banner">
            <FileCheck2 className="w-8 h-8 text-emerald-700 flex-shrink-0" />
            <div>
              <h3 className="font-display font-black text-xl uppercase text-[#0B0C0E]">Lease Active</h3>
              <p className="text-sm text-[#2C2F36] mt-2">
                Signed by <strong className="text-[#0B0C0E]">{booking.signature_name}</strong> on {new Date(booking.signed_at).toLocaleString()}.
                A copy is available for download anytime above.
              </p>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#0B0C0E] border-2 border-[#0B0C0E] text-xs">
                <Mini label="Lot" value={`#${lot.lot_number}`} />
                <Mini label="Rent" value={`${fmtMoney(lot.monthly_rent)}/mo`} />
                <Mini label="Term" value={`${booking.lease_term_months} mo`} />
                <Mini label="Start" value={booking.start_date} />
              </div>
              <button onClick={() => navigate(`/booking/${bookingId}`)} className="mt-6 border-2 border-[#0B0C0E] hover:bg-[#FACC15] text-[#0B0C0E] px-6 py-2.5 text-sm uppercase font-bold">View booking</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Mini = ({ label, value }) => (
  <div className="bg-white p-3">
    <div className="overline">{label}</div>
    <div className="mt-1 text-[#0B0C0E] font-medium">{value}</div>
  </div>
);
